import { formatARS } from '@gl/shared';
import { PencilLine, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Spinner } from '@/components/FullScreenLoader';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { CartLine } from './cart';
import type { CajaCategory, CajaItem } from './catalog-types';
import { MoneyInput } from './MoneyInput';

type AddLine = (line: Omit<CartLine, 'quantity' | 'key'> & { key?: string }) => void;

function priceLabel(item: CajaItem): string {
  if (item.variants.length === 0) return formatARS(item.priceCents);
  const prices = item.variants.map((v) => v.priceCents);
  const min = Math.min(...prices);
  return prices.some((p) => p !== min) ? `desde ${formatARS(min)}` : formatARS(min);
}

export function ProductGrid({
  categories,
  loading,
  disabled,
  canCustom,
  onAdd,
}: {
  categories: CajaCategory[];
  loading: boolean;
  disabled: boolean;
  canCustom: boolean;
  onAdd: AddLine;
}) {
  const [categoryId, setCategoryId] = useState<string | 'all'>('all');
  const [search, setSearch] = useState('');
  const [picking, setPicking] = useState<CajaItem | null>(null);
  const [custom, setCustom] = useState(false);

  const visibleCats = useMemo(
    () => categories.filter((c) => c.isActive && c.items.some((i) => i.isActive)),
    [categories],
  );
  const items = useMemo(() => {
    const q = search.trim().toLowerCase();
    return visibleCats
      .filter((c) => categoryId === 'all' || c.id === categoryId)
      .flatMap((c) => c.items.filter((i) => i.isActive))
      .filter((i) => !q || i.name.toLowerCase().includes(q));
  }, [visibleCats, categoryId, search]);

  const tap = (item: CajaItem) => {
    if (item.variants.length > 0) {
      setPicking(item);
      return;
    }
    onAdd({ itemId: item.id, name: item.name, unitPriceCents: item.priceCents });
  };

  return (
    <div className="grid gap-3">
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar producto…"
          className="pl-9"
          aria-label="Buscar producto"
        />
      </div>

      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:px-0">
        {[{ id: 'all', name: 'Todo' }, ...visibleCats].map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setCategoryId(c.id)}
            className={cn(
              'h-9 shrink-0 rounded-full border px-4 text-sm font-medium whitespace-nowrap transition-colors',
              categoryId === c.id ? 'border-primary bg-primary text-primary-foreground' : 'bg-background hover:bg-accent',
            )}
          >
            {c.name}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner className="size-6" />
        </div>
      ) : items.length === 0 && !canCustom ? (
        <p className="py-10 text-center text-sm text-muted-foreground">
          {search ? 'No hay productos con ese nombre.' : 'Todavía no hay productos en el menú.'}
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
          {items.map((item) => {
            const available = item.isAvailable && (item.variants.length === 0 || item.variants.some((v) => v.isAvailable));
            return (
              <button
                key={item.id}
                type="button"
                disabled={disabled || !available}
                onClick={() => tap(item)}
                className={cn(
                  'flex min-h-20 flex-col items-start justify-between gap-1 rounded-xl border bg-card p-3 text-left shadow-xs transition active:scale-[0.97]',
                  'hover:border-primary/50 disabled:pointer-events-none disabled:opacity-45',
                )}
              >
                <span className="line-clamp-2 text-sm leading-tight font-medium">{item.name}</span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {available ? priceLabel(item) : 'No disponible'}
                </span>
              </button>
            );
          })}
          {canCustom && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => setCustom(true)}
              className="flex min-h-20 flex-col items-start justify-between gap-1 rounded-xl border border-dashed p-3 text-left text-muted-foreground transition hover:border-primary/50 active:scale-[0.97] disabled:opacity-45"
            >
              <PencilLine className="size-4" />
              <span className="text-sm font-medium">Otro importe</span>
            </button>
          )}
        </div>
      )}

      <Dialog open={!!picking} onOpenChange={(o) => !o && setPicking(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{picking?.name}</DialogTitle>
            <DialogDescription>Elegí la variante</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            {picking?.variants.map((v) => (
              <button
                key={v.id}
                type="button"
                disabled={!v.isAvailable}
                onClick={() => {
                  onAdd({
                    itemId: picking.id,
                    variantId: v.id,
                    name: picking.name,
                    variantName: v.name,
                    unitPriceCents: v.priceCents,
                  });
                  setPicking(null);
                }}
                className="flex min-h-16 flex-col items-start justify-center rounded-xl border p-3 text-left transition hover:border-primary active:scale-[0.97] disabled:opacity-45"
              >
                <span className="font-medium">{v.name}</span>
                <span className="text-sm text-muted-foreground tabular-nums">
                  {v.isAvailable ? formatARS(v.priceCents) : 'No disponible'}
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <CustomLineDialog
        open={custom}
        onOpenChange={setCustom}
        onAdd={(name, cents) => onAdd({ key: `free:${Date.now()}`, name, unitPriceCents: cents, customPrice: true })}
      />
    </div>
  );
}

function CustomLineDialog({
  open,
  onOpenChange,
  onAdd,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onAdd: (name: string, cents: number) => void;
}) {
  const [name, setName] = useState('');
  const [cents, setCents] = useState<number | null>(null);
  const valid = name.trim().length > 0 && cents !== null && cents > 0;
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) {
          setName('');
          setCents(null);
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Otro importe</DialogTitle>
          <DialogDescription>Para algo que no está en el menú.</DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!valid) return;
            onAdd(name.trim(), cents!);
            onOpenChange(false);
            setName('');
            setCents(null);
          }}
        >
          <div className="grid gap-1.5">
            <Label htmlFor="custom-name">Concepto</Label>
            <Input id="custom-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Torta por encargo" />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="custom-price">Precio</Label>
            <MoneyInput id="custom-price" value={cents} onChange={setCents} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={!valid}>
              Agregar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
